import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Loader2, Wand2 } from 'lucide-react';

const LETTER_TYPES = [
  { value: 'Dispute Letter', label: 'Dispute Letter', description: 'Challenge inaccurate information on credit report' },
  { value: 'Goodwill Letter', label: 'Goodwill Letter', description: 'Request removal of negative item as goodwill gesture' },
  { value: 'Debt Validation Letter', label: 'Debt Validation Letter', description: 'Request proof of debt from collector' },
  { value: 'Inquiry Removal Letter', label: 'Inquiry Removal Letter', description: 'Request removal of unauthorized hard inquiry' },
  { value: 'Late Payment Removal Letter', label: 'Late Payment Removal Letter', description: 'Request removal of late payment record' },
  { value: 'Custom / Free Topic', label: 'Custom / Free Topic', description: 'Write about any topic — describe what you need and the letter will be generated' },
];

const BUREAUS = [
  { value: 'Experian', label: 'Experian' },
  { value: 'Equifax', label: 'Equifax' },
  { value: 'TransUnion', label: 'TransUnion' },
];

export default function LetterGenerator({ isOpen, onClose, clientId, onGenerated }) {
  const [letterType, setLetterType] = useState('');
  const [bureau, setBureau] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!letterType) {
      setError('Please select a letter type');
      return;
    }
    if (letterType === 'Custom / Free Topic' && !customTopic.trim()) {
      setError('Please describe the topic for your custom letter');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-letters/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: clientId,
          letter_type: letterType,
          bureau: bureau || null,
          account_name: accountName || null,
          account_number: accountNumber || null,
          dispute_reason: disputeReason || null,
          custom_topic: customTopic || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate letter');
      }

      const data = await response.json();
      
      // Reset form
      setLetterType('');
      setBureau('');
      setAccountName('');
      setAccountNumber('');
      setDisputeReason('');
      setCustomTopic('');
      
      // Callback with generated letter
      onGenerated(data);
      onClose();
    } catch (err) {
      setError(err.message || 'Error generating letter');
    } finally {
      setLoading(false);
    }
  };

  const selectedType = LETTER_TYPES.find(t => t.value === letterType);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-blue-600" />
            Generate Letter
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <div className="p-3 text-sm text-rose-700 bg-rose-50 rounded-lg">
              {error}
            </div>
          )}

          {/* Letter Type Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Letter Type *</Label>
            <Select value={letterType} onValueChange={setLetterType}>
              <SelectTrigger data-testid="select-letter-type">
                <SelectValue placeholder="Select letter type..." />
              </SelectTrigger>
              <SelectContent>
                {LETTER_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div>
                      <span className="font-medium">{type.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedType && (
              <p className="text-xs text-slate-500">{selectedType.description}</p>
            )}
          </div>

          {/* Custom Topic Field - shown only for Custom type */}
          {letterType === 'Custom / Free Topic' && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Describe Your Topic *</Label>
              <Textarea
                value={customTopic}
                onChange={(e) => setCustomTopic(e.target.value)}
                placeholder="Describe the letter topic — e.g., 'Request removal of medical collection from 2023', 'Dispute identity theft accounts', 'Request credit limit increase consideration'..."
                rows={3}
                className="text-sm"
                data-testid="input-custom-topic"
              />
            </div>
          )}

          {/* Bureau Selection (Optional) */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Credit Bureau (Optional)</Label>
            <Select value={bureau} onValueChange={setBureau}>
              <SelectTrigger data-testid="select-bureau">
                <SelectValue placeholder="Select bureau (optional)..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No specific bureau</SelectItem>
                {BUREAUS.map(b => (
                  <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Account Information (Optional) */}
          <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
            <p className="text-sm font-medium text-slate-700">Account Details (Optional)</p>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="account-name" className="text-xs">Account/Creditor Name</Label>
                <Input
                  id="account-name"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="e.g., ABC Collections"
                  className="text-sm"
                  data-testid="input-account-name"
                />
              </div>
              <div>
                <Label htmlFor="account-number" className="text-xs">Account Number</Label>
                <Input
                  id="account-number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="e.g., XXXX-1234"
                  className="text-sm"
                  data-testid="input-account-number"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="dispute-reason" className="text-xs">Reason/Issue (Optional)</Label>
              <Textarea
                id="dispute-reason"
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                placeholder="e.g., Account is not mine, balance is incorrect..."
                rows={2}
                className="text-sm"
                data-testid="input-dispute-reason"
              />
            </div>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Note:</strong> The letter will be auto-generated with the client's information and opened in edit mode. You can modify the content before saving or printing.
            </p>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button 
            onClick={handleGenerate} 
            className="bg-blue-600 hover:bg-blue-700"
            disabled={loading || !letterType || (letterType === 'Custom / Free Topic' && !customTopic.trim())}
            data-testid="generate-letter-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4 mr-2" />
                Generate Letter
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
